import React, { useEffect, useMemo, useRef, useState } from "react";
import { API_BASE } from "../api/config";
import "./LocationSelect.css";

export const ERP_SHOP_CODES = [
  "GMP",
  "OSHT",
  "OSPF",
  "MT",
  "JS",
  "JCHT",
  "JCPF",
  "MDYHT",
  "MDYPF",
];

export const ERP_WAREHOUSE_CODES = [
  "AYMHT",
  "AYMPF",
  "DNGHT",
  "DNGPF",
];

export const ERP_ALL_LOCATION_CODES = [
  ...ERP_SHOP_CODES,
  ...ERP_WAREHOUSE_CODES,
];

function normalizeLocation(row) {
  const code = String(row.code || "").trim().toUpperCase();
  const type = String(row.location_type || row.locationType || row.type || "SHOP")
    .trim()
    .toUpperCase();

  return {
    code,
    name: row.name || row.shopName || code,
    type: type === "WAREHOUSE" ? "WAREHOUSE" : "SHOP",
    brand: row.brand || "",
    groupCode: row.group_code || row.groupCode || row.group || "",
    isActive: row.is_active ?? row.isActive ?? true,
  };
}

async function fetchLocationMaster() {
  let response = await fetch(`${API_BASE}/api/erp/location-master`);

  if (!response.ok) {
    response = await fetch(`${API_BASE}/api/erp/locations?type=ALL&activeOnly=true`);
  }

  const data = await response.json();

  if (!response.ok || data.success === false) {
    throw new Error(data.message || "Failed to load locations");
  }

  const list = data.locations || data.rows || [];

  return list
    .map(normalizeLocation)
    .filter((loc) => loc.code && loc.isActive !== false);
}

export default function LocationSelect({
  value,
  onChange,
  type = "ALL",
  locationType,
  includeAll = true,
  allLabel,
  label,
  disabled = false,
  className = "",
  placeholder = "Select location",
}) {
  const wrapRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [dropdownStyle, setDropdownStyle] = useState({});

  const filterType = String(locationType || type || "ALL").trim().toUpperCase();

  function updateDropdownPosition() {
    if (!wrapRef.current) return;

    const rect = wrapRef.current.getBoundingClientRect();
    const margin = 16;
    const wantedWidth = 340;
    const width = Math.min(wantedWidth, window.innerWidth - margin * 2);

    let left = rect.right - width;

    if (left < margin) left = margin;
    if (left + width > window.innerWidth - margin) {
      left = window.innerWidth - width - margin;
    }

    const top = rect.bottom + 8;
    const maxHeight = Math.max(220, window.innerHeight - top - 18);

    setDropdownStyle({
      "--erp-location-dropdown-top": top + "px",
      "--erp-location-dropdown-left": left + "px",
      "--erp-location-dropdown-width": width + "px",
      "--erp-location-dropdown-max-height": maxHeight + "px",
    });
  }

  useEffect(() => {
    let alive = true;

    async function load() {
      setLoading(true);
      setLoadError("");

      try {
        const list = await fetchLocationMaster();

        if (alive) setLocations(list);
      } catch (err) {
        if (alive) {
          setLoadError(err.message || "Failed to load locations");

          setLocations([
            ...ERP_SHOP_CODES.map((code) => ({
              code,
              name: code,
              type: "SHOP",
              isActive: true,
            })),
            ...ERP_WAREHOUSE_CODES.map((code) => ({
              code,
              name: code,
              type: "WAREHOUSE",
              isActive: true,
            })),
          ]);
        }
      } finally {
        if (alive) setLoading(false);
      }
    }

    load();

    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (!open) return;

    updateDropdownPosition();

    window.addEventListener("resize", updateDropdownPosition);
    window.addEventListener("scroll", updateDropdownPosition, true);

    return () => {
      window.removeEventListener("resize", updateDropdownPosition);
      window.removeEventListener("scroll", updateDropdownPosition, true);
    };
  }, [open, locations.length, filterType]);

  useEffect(() => {
    function closeDropdown(event) {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(event.target)) setOpen(false);
    }

    document.addEventListener("mousedown", closeDropdown);
    return () => document.removeEventListener("mousedown", closeDropdown);
  }, []);

  const filteredLocations = useMemo(() => {
    if (filterType === "SHOP") return locations.filter((loc) => loc.type === "SHOP");
    if (filterType === "WAREHOUSE") return locations.filter((loc) => loc.type === "WAREHOUSE");
    return locations;
  }, [locations, filterType]);

  const shopLocations = filteredLocations.filter((loc) => loc.type === "SHOP");
  const warehouseLocations = filteredLocations.filter((loc) => loc.type === "WAREHOUSE");

  const finalAllLabel =
    allLabel ||
    (filterType === "SHOP"
      ? "All Shops"
      : filterType === "WAREHOUSE"
        ? "All Warehouses"
        : "All Locations");

  const selectedLocation = filteredLocations.find((loc) => loc.code === value);

  const buttonText =
    value === "ALL" || !value
      ? includeAll
        ? finalAllLabel
        : placeholder
      : selectedLocation
        ? `${selectedLocation.code} - ${selectedLocation.name}`
        : value;

  function selectValue(nextValue) {
    onChange?.(nextValue);
    setOpen(false);
  }

  function renderLocation(loc) {
    return (
      <div
        key={loc.code}
        className={`erpLocationOption ${value === loc.code ? "isSelected" : ""}`}
        onClick={() => selectValue(loc.code)}
        role="button"
        tabIndex={0}
      >
        <div className="erpLocCode">{loc.code}</div>
        <div className="erpLocInfo">
          <strong>{loc.name}</strong>
          <small>{loc.brand || loc.groupCode || loc.type}</small>
        </div>
        <div className={`erpLocBadge ${loc.type === "WAREHOUSE" ? "warehouse" : "shop"}`}>
          {loc.type === "WAREHOUSE" ? "W/S" : "SHOP"}
        </div>
      </div>
    );
  }

  return (
    <div ref={wrapRef} className={`erpLocationSelect ${open ? "isOpen" : ""} ${className || ""}`}>
      {label && <div className="erpLocationSelectLabel">{label}</div>}

      <div
        className={`erpLocationSelectBtn ${open ? "isOpen" : ""} ${disabled || loading ? "isDisabled" : ""}`}
        onClick={() => {
          if (disabled || loading) return;

          if (!open) {
            updateDropdownPosition();
            setOpen(true);
          } else {
            setOpen(false);
          }
        }}
        role="button"
        tabIndex={0}
      >
        <span>{loading ? "Loading..." : buttonText}</span>
        <em>⌄</em>
      </div>

      {open && (
        <div className="erpLocationDropdown" style={dropdownStyle}>
          {includeAll && (
            <div
              className={`erpLocationOption erpLocationAll ${value === "ALL" || !value ? "isSelected" : ""}`}
              onClick={() => selectValue("ALL")}
              role="button"
              tabIndex={0}
            >
              <div className="erpLocCode">ALL</div>
              <div className="erpLocInfo">
                <strong>{finalAllLabel}</strong>
                <small>Show combined inventory/report</small>
              </div>
            </div>
          )}

          {shopLocations.length > 0 && (
            <>
              <div className="erpLocationGroup">Shop Locations</div>
              {shopLocations.map(renderLocation)}
            </>
          )}

          {warehouseLocations.length > 0 && (
            <>
              <div className="erpLocationGroup warehouse">Warehouse / W/S</div>
              {warehouseLocations.map(renderLocation)}
            </>
          )}

          {filteredLocations.length === 0 && (
            <div className="erpLocationEmpty">No locations found</div>
          )}
        </div>
      )}

      {loadError && <div className="erpLocationSelectError">{loadError}</div>}
    </div>
  );
}
